/**
 * ProposalBubble — Assist Form Contract v1 (12/09/2026)
 *
 * Card de revisão de uma PROPOSTA (operations[]) no chat do Assist.
 * Estados: PROPOSED → APPLIED / PARTIALLY_APPLIED → UNDONE; PROPOSED →
 * REJECTED | STALE. NADA é persistido aqui: Aplicar = state local do
 * formulário via Bridge/adapter; Salvar continua sendo o botão canônico.
 *
 * Diff usa o valor ATUAL resolvido pelo adapter (nunca current_value do LLM).
 * Proposta stale (form mudou / instância mudou / superseded) NÃO tem botão
 * "Aplicar mesmo assim" no V1.
 *
 * Voz/números: todo valor numérico proposto aparece no diff antes de
 * qualquer aplicação — "3 vezes 6" reconhecido como 36 fica visível.
 */
import React, { useState, useMemo } from 'react';
import { Zap, Undo2, AlertTriangle } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';
import { useSportsLang } from '../../contexts/SportsLangContext';
import { useAssistFormBridge } from '../../contexts/AssistFormBridge';
import { ERROR_CODES, PROPOSAL_STATUS } from './contract/proposalContract';

const STALE_TEXT = {
  pt: {
    [ERROR_CODES.FORM_CHANGED]: 'O formulário mudou enquanto essa sugestão estava sendo preparada.',
    [ERROR_CODES.FORM_INSTANCE_CHANGED]: 'Essa sugestão era para outro formulário/registro.',
    [ERROR_CODES.SUPERSEDED]: 'Essa sugestão foi substituída por um pedido mais recente.',
    [ERROR_CODES.FORM_NOT_ACTIVE]: 'O formulário desta sugestão não está mais aberto.',
    [ERROR_CODES.PROPOSAL_ALREADY_APPLIED]: 'Sugestão já aplicada.',
    default: 'Sugestão desatualizada.',
  },
  en: {
    [ERROR_CODES.FORM_CHANGED]: 'The form changed while this suggestion was being prepared.',
    [ERROR_CODES.FORM_INSTANCE_CHANGED]: 'This suggestion targeted another form/record.',
    [ERROR_CODES.SUPERSEDED]: 'This suggestion was superseded by a newer request.',
    [ERROR_CODES.FORM_NOT_ACTIVE]: 'The form for this suggestion is no longer open.',
    [ERROR_CODES.PROPOSAL_ALREADY_APPLIED]: 'Suggestion already applied.',
    default: 'Suggestion is out of date.',
  },
};

const fmt = (v) => {
  if (v === undefined) return '—';
  if (v === null) return '∅';
  if (Array.isArray(v)) {
    // coleção de séries: resumo por linha
    return v.map((s, i) => (s && typeof s === 'object'
      ? `S${i + 1} ${s.reps ?? '?'}×${s.distance_m ?? '?'}m${s.stroke_code ? ` ${s.stroke_code}` : ''}${s.rest_seconds != null ? ` ↺${s.rest_seconds}s` : ''}`
      : String(s))).join(' · ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

export const ProposalBubble = ({ proposal, message, onRegenerate }) => {
  const { lang } = useSportsLang();
  const pt = lang !== 'en';
  const bridge = useAssistFormBridge();
  const [accepted, setAccepted] = useState(() => new Set(proposal.operations.map((o) => o.operationId)));
  const [result, setResult] = useState(null);   // resultado do apply
  const [undone, setUndone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Avaliação VIVA: o formulário pode ter mudado depois que o card apareceu.
  const staleness = useMemo(
    () => (result || dismissed ? { stale: false } : bridge.evaluateProposal(proposal)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [proposal, result, dismissed, bridge.activeFormId, bridge.currentGeneration()],
  );

  const rows = useMemo(() => proposal.operations.map((op) => {
    const actual = bridge.resolveCurrentValue(op.path, proposal.entity);
    return { op, current: actual !== undefined ? actual : op.current_value };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [proposal]);

  if (dismissed) {
    return <div className="py-1 text-sm opacity-60">{pt ? 'Sugestão descartada.' : 'Suggestion dismissed.'}</div>;
  }

  if (result) {
    const ok = result.status === 'applied' || result.status === 'partially_applied';
    const nApplied = result.appliedOperations?.length || 0;
    const rejected = (result.rejectedOperations || []).filter((r) => r.code !== ERROR_CODES.REJECTED_BY_USER);
    return (
      <div className="py-1" data-testid="proposal-result" data-status={undone ? 'undone' : result.status}>
        <div className={`flex items-center gap-2 text-sm font-medium ${ok ? 'text-green-700' : 'text-red-600'}`}>
          <Zap size={14} className="shrink-0" />
          {undone
            ? (pt ? 'Alteração desfeita.' : 'Change undone.')
            : ok
              ? (proposal.final
                ? (pt ? 'Pronto para revisão — revise no formulário e salve.' : 'Ready for review — review the form and save.')
                : (pt ? `${nApplied} alteração(ões) aplicada(s) ao formulário. Nada foi salvo.` : `${nApplied} change(s) applied to the form. Nothing saved.`))
              : (pt ? `Não aplicado (${result.code}).` : `Not applied (${result.code}).`)}
        </div>
        {rejected.length > 0 && (
          <div className="mt-1 text-xs text-red-600">
            {(pt ? 'Recusado pelo formulário: ' : 'Rejected by the form: ')}
            {rejected.map((r) => `${r.operationId?.slice(0, 8)} (${r.code}${r.reason ? `: ${r.reason}` : ''})`).join(' · ')}
          </div>
        )}
        {ok && !undone && (
          <button
            onClick={() => {
              const u = bridge.undoLastAppliedProposal(proposal.formInstanceId);
              if (u.status === 'undone') setUndone(true);
              else setResult({ ...result, undoError: u.code });
            }}
            className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold"
            style={{ borderColor: VUKZ.border }}
            data-testid="proposal-undo"
          >
            <Undo2 size={12} /> {pt ? 'Desfazer' : 'Undo'}
          </button>
        )}
        {result.undoError && (
          <div className="mt-1 text-xs text-amber-700">
            {pt ? `Não foi possível desfazer (${result.undoError}).` : `Could not undo (${result.undoError}).`}
          </div>
        )}
      </div>
    );
  }

  const staleMsg = staleness.stale
    ? ((STALE_TEXT[pt ? 'pt' : 'en'][staleness.reason]) || STALE_TEXT[pt ? 'pt' : 'en'].default)
    : null;

  const toggle = (id) => setAccepted((prev) => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });

  const apply = () => {
    const r = bridge.applyProposal(proposal, { acceptedOperationIds: [...accepted] });
    setResult(r);
  };

  return (
    <div className="py-1" data-testid="proposal-bubble" data-proposal-id={proposal.proposalId}
      data-stale={staleness.stale ? staleness.reason : undefined}>
      <div className="flex items-center gap-2 mb-2">
        <Zap size={14} className="text-amber-500 shrink-0" />
        <span className="text-sm font-semibold" style={{ color: VUKZ.text }}>
          {message || proposal.explanation || (pt ? 'Preparei uma sugestão de alteração' : 'I prepared a change suggestion')}
        </span>
      </div>
      {staleMsg && (
        <div className="mb-2 flex items-start gap-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid="proposal-stale">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
          <span>{staleMsg}</span>
        </div>
      )}
      <div className="rounded-lg border bg-neutral-50 px-3 py-2 mb-2 max-h-56 overflow-auto" style={{ borderColor: VUKZ.border }}>
        {rows.map(({ op, current }) => (
          <label key={op.operationId} className="flex items-start gap-2 text-xs py-1 cursor-pointer">
            <input type="checkbox" className="mt-0.5" disabled={staleness.stale}
              checked={accepted.has(op.operationId)} onChange={() => toggle(op.operationId)}
              data-testid={`proposal-op-${op.path}`} />
            <span className="flex-1 min-w-0">
              <span className="font-medium opacity-70">{op.op} · {op.path}</span>
              <span className="block break-words">
                <span className="opacity-60">{fmt(current)}</span>
                <span className="mx-1">→</span>
                <span className="font-semibold" style={{ color: VUKZ.text }}>{fmt(op.proposed_value)}</span>
              </span>
            </span>
          </label>
        ))}
        {proposal.operations.length === 0 && (
          <div className="text-xs opacity-60">{pt ? 'Nenhuma operação aplicável.' : 'No applicable operation.'}</div>
        )}
      </div>
      {proposal.missingFields?.length > 0 && (
        <div className="text-xs text-amber-700 mb-1">{(pt ? 'Faltou informar: ' : 'Missing: ') + proposal.missingFields.join(', ')}</div>
      )}
      {proposal.warnings?.length > 0 && (
        <div className="text-xs text-red-600 mb-1">{proposal.warnings.join(' · ')}</div>
      )}
      <div className="flex gap-2 mt-1">
        {!staleness.stale ? (
          <button onClick={apply} disabled={accepted.size === 0 || proposal.operations.length === 0}
            className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-bold disabled:opacity-40"
            data-testid="proposal-apply">
            {pt ? `Aplicar (${accepted.size}/${proposal.operations.length})` : `Apply (${accepted.size}/${proposal.operations.length})`}
          </button>
        ) : onRegenerate ? (
          <button onClick={onRegenerate}
            className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-bold"
            data-testid="proposal-regenerate">
            {pt ? 'Gerar novamente' : 'Regenerate'}
          </button>
        ) : null}
        <button onClick={() => { proposal.status = PROPOSAL_STATUS.REJECTED; setDismissed(true); }}
          className="px-3 py-1.5 rounded-lg border text-xs font-semibold" style={{ borderColor: VUKZ.border }}
          data-testid="proposal-dismiss">
          {pt ? 'Descartar' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};

export default ProposalBubble;
